import { useState, useEffect } from "react";
import API from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash } from "@phosphor-icons/react";
import SupplierPicker from "./SupplierPicker";

import DateInput from "./DateInput";
const emptyLine = () => ({ id: crypto.randomUUID(), title: "", description: "", quantity: 1, unit: "ea", unit_price: 0, total: 0 });

export default function WorkOrderEditDialog({ wo, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [lines, setLines] = useState([]);
  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [emergencies, setEmergencies] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [pickedSupplierId, setPickedSupplierId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!wo) return;
    setForm({
      source_type: wo.source_type || (wo.job_id ? "job" : "project"),
      project_id: wo.project_id || "",
      source_id: wo.job_id || "",
      vendor_id: wo.vendor_id || "",
      vendor_name: wo.vendor_name || "",
      vendor_company: wo.vendor_company || "",
      vendor_email: wo.vendor_email || "",
      vendor_phone: wo.vendor_phone || "",
      priority: wo.priority || "medium",
      due_date: wo.due_date || "",
      scope: wo.scope || "",
      instructions: wo.instructions || "",
      compliance_requirements: wo.compliance_requirements || "",
    });
    setPickedSupplierId("");
    const initLines = (wo.line_items || []).map(li => ({
      id: li.id || crypto.randomUUID(),
      title: li.title || li.description || "",
      description: li.description || "",
      quantity: Number(li.quantity || 1),
      unit: li.unit || "ea",
      unit_price: Number(li.unit_price || 0),
      total: Number(li.total || li.budget || 0),
    }));
    setLines(initLines.length ? initLines : [emptyLine()]);
    API.get("/projects").then(({ data }) => setProjects(data.projects || [])).catch(() => {});
    API.get("/jobs?limit=200").then(({ data }) => setJobs(data.jobs || [])).catch(() => {});
    API.get("/emergency-jobs?limit=200").then(({ data }) => setEmergencies(data.jobs || [])).catch(() => {});
    API.get("/suppliers?limit=500").then(({ data }) => setSuppliers(Array.isArray(data) ? data : data.suppliers || [])).catch(() => {});
  }, [wo]);

  if (!wo || !form) return <Dialog open={false}><DialogContent /></Dialog>;

  const sourceList = form.source_type === "job" ? jobs : emergencies;
  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const updateLine = (idx, patch) => {
    setLines(prev => prev.map((l, i) => {
      if (i !== idx) return l;
      const merged = { ...l, ...patch };
      if (patch.quantity !== undefined || patch.unit_price !== undefined) {
        merged.total = Number(((Number(merged.quantity) || 0) * (Number(merged.unit_price) || 0)).toFixed(2));
      }
      return merged;
    }));
  };
  const grandTotal = lines.reduce((s, l) => s + (Number(l.total) || 0), 0);

  const pickSupplier = (s) => {
    if (!s) { setPickedSupplierId(""); return; }
    setPickedSupplierId(s.id);
    set({
      vendor_id: s.user_id || "",
      vendor_name: s.contact_name || s.name || "",
      vendor_company: s.company_name || s.name || "",
      vendor_email: s.email || "",
      vendor_phone: s.phone || "",
    });
  };

  const save = async () => {
    if (form.source_type === "project" && !form.project_id) { toast.error("Pick a project to issue against"); return; }
    if (form.source_type !== "project" && !form.source_id) { toast.error("Pick a job to issue against"); return; }
    setSaving(true);
    try {
      const line_items = lines.filter(l => (l.title || "").trim()).map(l => ({
        ...l,
        description: l.description || l.title,
        quantity: Number(l.quantity) || 0,
        unit_price: Number(l.unit_price) || 0,
        total: Number(l.total) || 0,
        budget: Number(l.total) || 0,
      }));
      await API.put(`/works-orders/${wo.id}`, {
        source_type: form.source_type,
        project_id: form.source_type === "project" ? form.project_id : "",
        source_id: form.source_type === "project" ? "" : form.source_id,
        vendor_id: form.vendor_id,
        vendor_name: form.vendor_name,
        vendor_company: form.vendor_company,
        vendor_email: form.vendor_email,
        vendor_phone: form.vendor_phone,
        priority: form.priority,
        due_date: form.due_date,
        scope: form.scope,
        instructions: form.instructions,
        compliance_requirements: form.compliance_requirements,
        line_items,
        total_budget: Number(grandTotal.toFixed(2)),
      });
      const { data: fresh } = await API.get(`/works-orders/${wo.id}`);
      toast.success(`Works Order ${wo.wo_number} updated`);
      onSaved?.(fresh);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update works order");
    } finally { setSaving(false); }
  };

  const pickedSupplier = suppliers.find(s => s.id === pickedSupplierId);
  const noPortal = pickedSupplier && !pickedSupplier.user_id;

  return (
    <Dialog open={!!wo} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="wo-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">Edit Works Order — <span className="font-mono">{wo.wo_number}</span></DialogTitle>
          <DialogDescription>Update anything on this draft before it goes to the supplier.</DialogDescription>
        </DialogHeader>

        {/* ── Issue against ── */}
        <div className="border border-border rounded-md p-3 space-y-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Issued Against</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.source_type} onValueChange={v => set({ source_type: v, project_id: "", source_id: "" })}>
                <SelectTrigger data-testid="wo-edit-source-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="project">Project</SelectItem>
                  <SelectItem value="job">Job</SelectItem>
                  <SelectItem value="emergency">Emergency Job</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{form.source_type === "project" ? "Project" : form.source_type === "job" ? "Job" : "Emergency Job"} *</Label>
              {form.source_type === "project" ? (
                <Select value={form.project_id} onValueChange={v => set({ project_id: v })}>
                  <SelectTrigger data-testid="wo-edit-source-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Select value={form.source_id} onValueChange={v => set({ source_id: v })}>
                  <SelectTrigger data-testid="wo-edit-source-select"><SelectValue placeholder={form.source_type === "job" ? "Select job" : "Select emergency job"} /></SelectTrigger>
                  <SelectContent>
                    {sourceList.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">None found</div>}
                    {sourceList.map(j => <SelectItem key={j.id} value={j.id}>{j.reference ? `${j.reference} — ` : ""}{j.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">Changing this re-pulls the property address and client from the newly linked {form.source_type === "project" ? "project" : "job"}.</p>
        </div>

        {/* ── Supplier ── */}
        <div className="border border-border rounded-md p-3 space-y-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Supplier</p>
          <SupplierPicker
            suppliers={suppliers}
            selectedId={pickedSupplierId}
            onPick={pickSupplier}
            label="Search your supplier list (fills the details below)"
            testIdPrefix="wo-edit-supplier"
          />
          {noPortal && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400">This supplier has no portal account — they'll receive the works order by email only.</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Contact Name</Label><Input value={form.vendor_name} onChange={e => set({ vendor_name: e.target.value })} placeholder="e.g. Alan Smith" data-testid="wo-edit-vendor-name" /></div>
            <div><Label className="text-xs">Company</Label><Input value={form.vendor_company} onChange={e => set({ vendor_company: e.target.value })} placeholder="e.g. AG Plumbing Ltd" data-testid="wo-edit-vendor-company" /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" value={form.vendor_email} onChange={e => set({ vendor_email: e.target.value })} placeholder="supplier@company.com" data-testid="wo-edit-vendor-email" /></div>
            <div><Label className="text-xs">Phone</Label><Input value={form.vendor_phone} onChange={e => set({ vendor_phone: e.target.value })} placeholder="07..." data-testid="wo-edit-vendor-phone" /></div>
          </div>
        </div>

        {/* ── Details ── */}
        <div className="border border-border rounded-md p-3 space-y-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Details</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={form.priority} onValueChange={v => set({ priority: v })}>
                <SelectTrigger data-testid="wo-edit-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{["low", "medium", "high", "critical"].map(p => <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Required Completion Date</Label><DateInput value={form.due_date} onChange={e => set({ due_date: e.target.value })} data-testid="wo-edit-due-date" /></div>
          </div>
          <div><Label className="text-xs">Scope of Works</Label><Textarea rows={3} value={form.scope} onChange={e => set({ scope: e.target.value })} data-testid="wo-edit-scope" /></div>
          <div><Label className="text-xs">Special Instructions</Label><Textarea rows={2} value={form.instructions} onChange={e => set({ instructions: e.target.value })} data-testid="wo-edit-instructions" /></div>
          <div><Label className="text-xs">Compliance Requirements</Label><Textarea rows={2} value={form.compliance_requirements} onChange={e => set({ compliance_requirements: e.target.value })} data-testid="wo-edit-compliance" /></div>
        </div>

        {/* ── Line items ── */}
        <div className="border border-border rounded-md p-3 space-y-2" data-testid="wo-edit-lines">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Cost Lines</p>
          <div className="hidden md:grid grid-cols-[1fr_70px_70px_110px_110px_36px] gap-2 px-1 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            <span>Description</span><span className="text-right">Qty</span><span>Unit</span><span className="text-right">Unit Price (£)</span><span className="text-right">Total (£)</span><span></span>
          </div>
          {lines.map((line, idx) => (
            <div key={line.id} className="grid grid-cols-2 md:grid-cols-[1fr_70px_70px_110px_110px_36px] gap-2 items-start border border-border rounded-md p-2 bg-muted/10" data-testid={`wo-edit-line-${idx}`}>
              <div className="col-span-2 md:col-span-1">
                <Input placeholder="Description (e.g. Replace bedroom radiator)" value={line.title || ""} onChange={e => updateLine(idx, { title: e.target.value, description: e.target.value })} data-testid={`wo-edit-line-title-${idx}`} />
              </div>
              <Input type="number" step="any" value={line.quantity} onChange={e => updateLine(idx, { quantity: e.target.value })} className="text-right" data-testid={`wo-edit-line-qty-${idx}`} />
              <Input value={line.unit} onChange={e => updateLine(idx, { unit: e.target.value })} placeholder="ea" data-testid={`wo-edit-line-unit-${idx}`} />
              <Input type="number" step="0.01" value={line.unit_price} onChange={e => updateLine(idx, { unit_price: e.target.value })} className="text-right" data-testid={`wo-edit-line-price-${idx}`} />
              <Input type="number" step="0.01" value={line.total} onChange={e => updateLine(idx, { total: e.target.value })} className="text-right font-semibold" data-testid={`wo-edit-line-total-${idx}`} />
              <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => setLines(prev => prev.filter((_, i) => i !== idx))} data-testid={`wo-edit-line-remove-${idx}`}><Trash size={14} /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setLines(prev => [...prev, emptyLine()])} className="w-full" data-testid="wo-edit-line-add">
            <Plus size={14} className="mr-1" /> Add Line
          </Button>
          <div className="flex items-center justify-end pt-2 border-t border-border text-sm">
            <span className="text-muted-foreground mr-2">Total:</span>
            <span className="text-lg font-bold" data-testid="wo-edit-grand-total">£{grandTotal.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="wo-edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="wo-edit-save">{saving ? "Saving..." : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
